import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import Layout from "../components/Layout";
import { WmPart } from "../lib/wmParts";
import api from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { ExportXlsxButton } from "../components/ExportXlsxButton";
import { SpreadsheetModal } from "../components/SpreadsheetModal";
import { xlsxDate } from "../lib/exportXlsx";
import {
  CaretRight, CaretDown, Package, FileText, Truck, XCircle, Clock,
  MagnifyingGlass, ClockClockwise, DownloadSimple, WarningCircle, Table,
} from "@phosphor-icons/react";

const money = (n, c) => `${c === "USD" ? "$" : c === "INR" ? "₹" : c === "GBP" ? "£" : ""}${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => (Number(n) || 0).toLocaleString();

/** Human "as of" time for the ERP-stale banner: HH:MM if today, else "DD Mon HH:MM". */
function fmtAsOf(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const t = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay ? t : `${d.toLocaleDateString([], { day: "2-digit", month: "short" })} ${t}`;
}

const STATUS = {
  staged:       { label: "Staged",           bg: "#F1F5F9", fg: "#475569", icon: Clock },
  planned:      { label: "Planned",          bg: "#FEF3C7", fg: "#92400E", icon: Clock },
  ready_export: { label: "Ready for Export", bg: "#EDE9FE", fg: "#5B21B6", icon: Package },
  dispatched:   { label: "Dispatched",       bg: "#DCFCE7", fg: "#166534", icon: Truck },
  cancelled:    { label: "Cancelled",        bg: "#FEE2E2", fg: "#991B1B", icon: XCircle },
};
const STATUS_ORDER = ["staged", "planned", "ready_export", "dispatched", "cancelled"];

const OTD = {
  late:    { label: "Late",    bg: "#FEE2E2", fg: "#B91C1C" },
  on_time: { label: "On Time", bg: "#DCFCE7", fg: "#166534" },
  early:   { label: "Early",   bg: "#DBEAFE", fg: "#1E40AF" },
};

/** OTD bucket for one line: target = due − offset; Δ = dispatch − target. */
function lineOtd(dispatchDate, dueDate, offset) {
  if (!dispatchDate || !dueDate) return null;
  const ref = new Date(dispatchDate); const due = new Date(dueDate);
  if (isNaN(ref) || isNaN(due)) return null;
  const target = new Date(due); target.setDate(target.getDate() - (Number(offset) || 0));
  const delta = Math.round((ref - target) / 86400000);
  if (delta > 0) return { status: "late", days: delta };
  if (delta < 0) return { status: "early", days: delta };
  return { status: "on_time", days: 0 };
}

function OtdChip({ status, days, testid }) {
  if (!status) return <span className="muted" style={{ fontSize: 11 }}>—</span>;
  const o = OTD[status];
  const label = status === "late" ? `${days}d late` : status === "early" ? `${Math.abs(days)}d early` : "on time";
  return (
    <span className="jde-badge" data-testid={testid} style={{ background: o.bg, color: o.fg, fontWeight: 700 }}>{o.label}
      <span style={{ fontWeight: 500, marginLeft: 4, opacity: 0.85 }}>· {label}</span>
    </span>
  );
}

export default function DispatchRegister() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});
  const [lines, setLines] = useState({});
  const [lineBusy, setLineBusy] = useState({});

  // filters
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("");
  const [otdF, setOtdF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [src, setSrc] = useState("all");   // all | app | legacy (portal-native vouchers)
  const [windowDays, setWindowDays] = useState("120");  // legacy/invoice-only rolling window; "0" = all-time
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const debounce = useRef(null);
  const warmTimer = useRef(null);
  const pollCount = useRef(0);
  const [warming, setWarming] = useState(false);
  const [asOf, setAsOf] = useState(null);
  const [erpUnresolved, setErpUnresolved] = useState(false);
  const [erpDown, setErpDown] = useState(false);
  const [erpError, setErpError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (typeF) p.set("type", typeF);
    if (otdF) p.set("otd", otdF);
    if (statusF) p.set("status", statusF);
    if (src && src !== "all") p.set("src", src);
    if (windowDays && windowDays !== "120") p.set("legacy_days", windowDays);
    api.get(`/dispatch/register?${p.toString()}`)
      .then((r) => {
        // The register is a heavy live-ERP build. It's served instantly from a persisted snapshot
        // (Mongo) / in-memory cache; while a fresh build runs in the background the API sets
        // warming:true. Show whatever rows we have and poll (bounded) to pick up the fresh build.
        const rows = r.data.rows || [];
        setRows(rows); setCounts(r.data.counts || {}); setTotal(r.data.total || 0);
        setErpUnresolved(!!r.data.erp_status_unresolved);
        setErpDown(!!r.data.erp_down);
        setErpError(r.data.erp_error || null);
        setAsOf(r.data.as_of || null);
        if (r.data.warming) {
          setWarming(rows.length === 0);   // only show the "building" banner if we truly have nothing
          if (warmTimer.current) clearTimeout(warmTimer.current);
          if (pollCount.current < 20) { pollCount.current += 1; warmTimer.current = setTimeout(load, 6000); }
        } else {
          setWarming(false);
          pollCount.current = 0;
        }
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load dispatch register"))
      .finally(() => setLoading(false));
  }, [q, typeF, otdF, statusF, src, windowDays]);

  useEffect(() => () => { if (warmTimer.current) clearTimeout(warmTimer.current); }, []);

  // Force a live-ERP rebuild of the register (picks up customer-name / data corrections without
  // waiting for the periodic background warmer). Non-blocking: kicks the rebuild server-side then
  // polls via the normal warming flow until the fresh rows land.
  const forceRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const p = new URLSearchParams();
      if (src && src !== "all") p.set("src", src);
      if (windowDays && windowDays !== "120") p.set("legacy_days", windowDays);
      await api.post(`/dispatch/register/refresh?${p.toString()}`);
      toast.success("Rebuilding the register from live ERP — it will update here shortly.");
      pollCount.current = 0;
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to trigger refresh");
    } finally {
      setRefreshing(false);
    }
  }, [src, windowDays, load]);

  const [exporting, setExporting] = useState(false);
  const exportDetailed = useCallback(async () => {
    setExporting(true);
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (typeF) p.set("type", typeF);
    if (otdF) p.set("otd", otdF);
    if (statusF) p.set("status", statusF);
    if (src) p.set("src", src);
    p.set("legacy_days", windowDays);
    try {
      const r = await api.get(`/dispatch/register/export.xlsx?${p.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dispatch_register_detail_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to export detailed register");
    } finally {
      setExporting(false);
    }
  }, [q, typeF, otdF, statusF, src, windowDays]);

  const [reconciling, setReconciling] = useState(false);
  const downloadReconcile = useCallback(async () => {
    setReconciling(true);
    try {
      const r = await api.get(`/dispatch/reconcile.xlsx?legacy_days=120`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dispatch_po_reconciliation_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to build reconciliation");
    } finally {
      setReconciling(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(load, 250);
    return () => clearTimeout(debounce.current);
  }, [load]);

  const toggle = async (d) => {
    const planId = d.plan_id;
    const willOpen = !open[planId];
    setOpen((s) => ({ ...s, [planId]: willOpen }));
    if (willOpen && !lines[planId]) {
      setLineBusy((s) => ({ ...s, [planId]: true }));
      try {
        const r = d.legacy
          ? await api.get(`/dispatch/register/legacy-lines?voc=${d.voc_no}`)
          : d.invoice_only
          ? await api.get(`/dispatch/register/invoice-lines?inv=${d.inv_id}`)
          : await api.get(`/dispatch/plan/${planId}`);
        setLines((s) => ({ ...s, [planId]: r.data.lines || [] }));
      } catch (e) {
        toast.error("Could not load line items");
        setLines((s) => ({ ...s, [planId]: [] }));
      } finally { setLineBusy((s) => ({ ...s, [planId]: false })); }
    }
  };

  const th = { textAlign: "left", padding: "8px 8px", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, color: "#64748B", borderBottom: "1px solid var(--jde-border)", whiteSpace: "nowrap", background: "#F8FAFC" };
  const td = { padding: "8px 8px", fontSize: 12, borderBottom: "1px solid #EEF1F4", whiteSpace: "nowrap" };

  const clearAll = () => { setQ(""); setTypeF(""); setOtdF(""); setStatusF(""); };
  const hasFilters = q || typeF || otdF || statusF;

  const SORT_ACCESS = {
    type: (r) => (r.is_export ? 1 : 0),
    plan: (r) => r.plan_id,
    customer: (r) => (r.customer_name || "").toLowerCase(),
    custpo2: (r) => (r.cust_po2 || "").toLowerCase(),
    date: (r) => r.dispatch_date || "",
    invoice: (r) => (r.inv_no || "").toString(),
    qty: (r) => Number(r.tot_qty) || 0,
    amount: (r) => Number(r.tot_amount) || 0,
    otd: (r) => (r.otd_days == null ? -1e12 : r.otd_days),
    status: (r) => STATUS_ORDER.indexOf(r.status),
  };
  const sorted = [...rows].sort((a, b) => {
    const f = SORT_ACCESS[sort.key] || SORT_ACCESS.date;
    const x = f(a), y = f(b);
    if (x < y) return sort.dir === "asc" ? -1 : 1;
    if (x > y) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });
  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });

  const exportColumns = [
    { header: "Type", value: (d) => (d.is_export ? "Export" : "Domestic") },
    { header: "Dispatch Plan / Voc", value: (d) => d.invoice_only ? `INV ${d.inv_no || ""}` : d.legacy ? `Voc# ${d.voc_no}` : `DP-${(d.plan_id || "").slice(-6).toUpperCase()}` },
    { header: "Source", value: (d) => (d.invoice_only ? "Invoice-only" : d.legacy ? "Legacy (portal)" : "App plan") },
    { header: "ERP Voc #", value: (d) => d.voc_no || "" },
    { header: "Customer", value: (d) => d.customer_name || "" },
    { header: "Transfer Party", value: (d) => d.tp_name || "" },
    { header: "End Customer PO Number", value: (d) => d.cust_po2 || "" },
    { header: "Dispatch Date", value: (d) => xlsxDate(d.dispatch_date) },
    { header: "Invoice #", value: (d) => d.inv_no || "" },
    { header: "Total Qty", value: (d) => Number(d.tot_qty) || 0, numeric: true },
    { header: "Amount", value: (d) => Number(d.tot_amount) || 0, numeric: true },
    { header: "Currency", value: (d) => d.currency || "" },
    { header: "CFS Cut-Off", value: (d) => (d.cfs_cutoff_date ? xlsxDate(d.cfs_cutoff_date) : "") },
    { header: "Must Ship By", value: (d) => (d.must_ship_by ? xlsxDate(d.must_ship_by) : "") },
    { header: "OTD Target Date", value: (d) => (d.otd_target ? xlsxDate(d.otd_target) : "") },
    { header: "OTD", value: (d) => d.otd_status ? (d.otd_status === "late" ? `${d.otd_days}d late` : d.otd_status === "early" ? `${Math.abs(d.otd_days)}d early` : "on time") : "" },
    { header: "Status", value: (d) => (STATUS[d.status]?.label || d.status || "") },
  ];

  const caret = (key) => sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
  const Sh = ({ k, children, align }) => (
    <th style={{ ...th, textAlign: align || "left", cursor: k ? "pointer" : "default", userSelect: "none" }}
      onClick={k ? () => toggleSort(k) : undefined} data-testid={k ? `dr-sort-${k}` : undefined}>
      {children}<span style={{ color: "#0EA5E9" }}>{k ? caret(k) : ""}</span>
    </th>
  );

  return (
    <Layout breadcrumb={["Home", "Dispatch", "Register"]}>
      <div className="jde-page">
        {/* Compact filter strip: status pills + search + selects on ONE row */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="dr-status-tiles">
            {STATUS_ORDER.map((k) => {
              const st = STATUS[k]; const active = statusF === k;
              return (
                <button key={k} onClick={() => setStatusF(active ? "" : k)} data-testid={`dr-tile-${k}`} title={`Filter: ${st.label}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 9px", borderRadius: 6, cursor: "pointer",
                    fontSize: 11.5, fontWeight: 600, background: active ? st.fg : st.bg, color: active ? "#fff" : st.fg,
                    border: `1px solid ${active ? st.fg : "transparent"}`, transition: "background-color .15s" }}>
                  <st.icon size={13} weight="bold" />
                  {st.label}
                  <span style={{ fontWeight: 800, background: active ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.06)", borderRadius: 10, padding: "0 6px" }}>{counts[k] || 0}</span>
                </button>
              );
            })}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ position: "relative", flex: "0 1 300px", minWidth: 200 }}>
            <MagnifyingGlass size={13} weight="bold" style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#94A3B8" }} />
            <input className="jde-input" style={{ width: "100%", paddingLeft: 28, height: 32, fontSize: 12.5, paddingTop: 0, paddingBottom: 0, lineHeight: "30px" }} placeholder="Search customer, PO #, part, plan, invoice #…"
              value={q} onChange={(e) => setQ(e.target.value)} data-testid="dr-search" />
          </div>
          <select className="jde-input" style={{ height: 32, fontSize: 12.5, paddingTop: 0, paddingBottom: 0, lineHeight: "30px", width: 130, flex: "0 0 auto" }} value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="dr-filter-type">
            <option value="">All types</option>
            <option value="export">Export</option>
            <option value="domestic">Domestic</option>
          </select>
          <select className="jde-input" style={{ height: 32, fontSize: 12.5, paddingTop: 0, paddingBottom: 0, lineHeight: "30px", width: 120, flex: "0 0 auto" }} value={otdF} onChange={(e) => setOtdF(e.target.value)} data-testid="dr-filter-otd">
            <option value="">All OTD</option>
            <option value="late">Late</option>
            <option value="on_time">On Time</option>
            <option value="early">Early</option>
          </select>
          <select className="jde-input" style={{ height: 32, fontSize: 12.5, paddingTop: 0, paddingBottom: 0, lineHeight: "30px", width: 150, flex: "0 0 auto" }} value={src} onChange={(e) => setSrc(e.target.value)} data-testid="dr-filter-src" title="Source: app plans vs legacy portal vouchers">
            <option value="all">All sources</option>
            <option value="app">App plans</option>
            <option value="legacy">Legacy (portal)</option>
          </select>
          <select className="jde-input" style={{ height: 32, fontSize: 12.5, paddingTop: 0, paddingBottom: 0, lineHeight: "30px", width: 150, flex: "0 0 auto" }} value={windowDays} onChange={(e) => setWindowDays(e.target.value)} data-testid="dr-filter-window" title="How far back to include legacy ERP vouchers & invoice-only shipments (app plans always show)">
            <option value="120">Last 120 days</option>
            <option value="365">Last 365 days</option>
            <option value="730">Last 2 years</option>
            <option value="0">All time</option>
          </select>
          {hasFilters && <button className="jde-btn jde-btn-sm" onClick={clearAll} data-testid="dr-clear-filters">Clear</button>}
          <button className="jde-btn jde-btn-sm" onClick={forceRefresh} disabled={refreshing} data-testid="dr-refresh"
            title="Rebuild the register from live ERP now — picks up customer-name / data corrections without waiting for the periodic refresh"
            style={{ display: "inline-flex", alignItems: "center", gap: 5, flex: "0 0 auto" }}>
            <ClockClockwise size={13} weight="bold" /> {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {erpUnresolved && (
          <div data-testid="dr-erp-unresolved-banner" style={{ margin: "0 0 12px", padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, fontSize: 12.5, lineHeight: 1.5, color: "#7F1D1D", display: "flex", alignItems: "center", gap: 10 }}>
            <WarningCircle size={18} weight="fill" style={{ flexShrink: 0, color: "#DC2626" }} />
            <span><strong>Live ERP status temporarily unavailable.</strong> The dispatched/invoiced status couldn't be confirmed right now, so shipments that are actually <em>Dispatched</em> may be showing as <em>Planned</em>. This is an ERP connection issue — not your data. Click <strong>Refresh</strong> in a moment to re-resolve.</span>
          </div>
        )}


        <div className="jde-panel">
          <div className="jde-panel-header">
            <div className="jde-panel-title" style={{ flex: "0 0 auto" }}>DISPATCH REGISTER ({rows.length}{loading ? "…" : ""}{hasFilters ? ` of ${total}` : ""})</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginLeft: "auto", justifyContent: "flex-end" }}>
              <span className="muted" style={{ fontSize: 11, flex: "0 1 auto" }}>OTD target = due date − customer ex-factory offset</span>
              <button className="jde-btn jde-btn-sm" onClick={exportDetailed} disabled={exporting} data-testid="dr-export-detail-xlsx"
                title="Download the full register with every dispatch's line items expanded (collapsible groups in Excel)"
                style={{ display: "inline-flex", alignItems: "center", gap: 5, flex: "0 0 auto", whiteSpace: "nowrap" }}>
                <DownloadSimple size={13} weight="bold" /> {exporting ? "Building…" : "Export (with lines)"}
              </button>
              <button className="jde-btn jde-btn-sm" onClick={downloadReconcile} disabled={reconciling} data-testid="dr-reconcile-xlsx"
                title="Download a reconciliation of the Dispatch Register vs the PO table — flags PO undercounts, register double-counts and item-code mismatches"
                style={{ display: "inline-flex", alignItems: "center", gap: 5, flex: "0 0 auto", whiteSpace: "nowrap" }}>
                <DownloadSimple size={13} weight="bold" /> {reconciling ? "Matching…" : "Reconcile vs PO table"}
              </button>
              <button className="jde-btn jde-btn-sm jde-btn-primary" onClick={() => setSheetOpen(true)} data-testid="open-spreadsheet"
                title="Open the current register view in a spreadsheet — drag-select cells to total, analyze, add formulas"
                style={{ display: "inline-flex", alignItems: "center", gap: 5, flex: "0 0 auto", whiteSpace: "nowrap" }}>
                <Table size={13} weight="bold" /> Open in Spreadsheet
              </button>
              <div style={{ flex: "0 0 auto" }}>
                <ExportXlsxButton filename="dispatch-register" columns={exportColumns} rows={sorted} sheetName="Dispatch Register" testid="dr-export-xlsx" />
              </div>
            </div>
          </div>
          <div className="jde-panel-body" style={{ padding: 0, overflowX: "auto" }}>
            {erpDown && rows.length > 0 && (
              <div data-testid="dr-erp-stale-banner" role="status" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", background: "#FEF3C7", borderBottom: "1px solid #FDE68A", color: "#92400E", fontSize: 12.5, fontWeight: 600 }}>
                <WarningCircle size={18} weight="fill" style={{ flex: "0 0 auto", color: "#B45309" }} />
                <span>ERP link unavailable — showing last synced data{asOf ? ` as of ${fmtAsOf(asOf)}` : ""}. Live updates paused.</span>
                <button className="jde-btn jde-btn-sm" onClick={forceRefresh} disabled={refreshing} data-testid="dr-erp-retry"
                  style={{ marginLeft: "auto", flex: "0 0 auto" }}>
                  <ClockClockwise size={12} weight="bold" /> {refreshing ? "Retrying…" : "Retry"}
                </button>
              </div>
            )}
            {erpDown && rows.length === 0 && (
              <div data-testid="dr-erp-down-empty" role="alert" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: "48px 20px", textAlign: "center" }}>
                <WarningCircle size={40} weight="fill" style={{ color: "#DC2626" }} />
                <div style={{ fontSize: 15, fontWeight: 800, color: "#991B1B" }}>ERP connection is down</div>
                <div style={{ fontSize: 13, color: "#64748B", maxWidth: 460 }}>Live data can't be loaded right now. The register will refresh automatically once the link is restored.{erpError ? ` (${erpError})` : ""}</div>
                <button className="jde-btn jde-btn-primary" onClick={forceRefresh} disabled={refreshing} data-testid="dr-erp-retry-empty"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <ClockClockwise size={13} weight="bold" /> {refreshing ? "Retrying…" : "Retry"}
                </button>
              </div>
            )}
            {!(erpDown && rows.length === 0) && (
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }} data-testid="dispatch-register-table">
              <thead>
                <tr>
                  <th style={{ ...th, width: 34 }}></th>
                  <Sh k="type" align="center">Type</Sh>
                  <Sh k="plan">Dispatch Plan ID</Sh>
                  <Sh k="customer">Customer</Sh>
                  <Sh k="custpo2">End Customer PO</Sh>
                  <Sh k="date">Dispatch Date</Sh>
                  <Sh k="invoice">Invoice #</Sh>
                  <Sh k="qty" align="right">Total Qty</Sh>
                  <Sh k="amount" align="right">Amount</Sh>
                  <th style={{ ...th, textAlign: "center" }}>Must Ship By</th>
                  <th style={{ ...th, textAlign: "center" }} title="OTD target date the on-time grade is measured against (CFS-based Must Ship By, else customer due date − ex-factory offset)">Target Date</th>
                  <Sh k="otd" align="center">OTD</Sh>
                  <Sh k="status" align="center">Status</Sh>
                </tr>
              </thead>
              <tbody>
                {sorted.map((d) => {
                  const st = STATUS[d.status] || STATUS.planned;
                  const isOpen = !!open[d.plan_id];
                  return (
                    <Fragment key={d.plan_id}>
                      <tr key={`h-${d.plan_id}`} onClick={() => toggle(d)} style={{ cursor: "pointer", background: isOpen ? "#F1F5F9" : "white" }} data-testid={`disp-reg-row-${d.plan_id}`}>
                        <td style={td}>{isOpen ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />}</td>
                        <td style={{ ...td, textAlign: "center" }}>
                          <span className="jde-badge" style={{ fontSize: 9, fontWeight: 700, color: "white", background: d.is_export ? "#7C3AED" : "#0F766E" }}>
                            {d.is_export ? "EXPORT" : "DOMESTIC"}
                          </span>
                        </td>
                        <td style={td}>
                          {d.invoice_only
                            ? <><span className="text-mono" style={{ fontWeight: 700 }} title={d.voc_no ? `Invoice ${d.inv_no || ""} · ERP Voc #${d.voc_no}` : "Invoice-only shipment (no dispatch plan / voucher)"}>INV {d.inv_no || "—"}</span>
                                <div className="muted" style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 4, marginTop: 1 }}>
                                  <span className="jde-badge" style={{ fontSize: 8.5, background: "#DBEAFE", color: "#1E40AF", fontWeight: 700 }}>INVOICE</span>
                                  {d.voc_no ? <>billed · Voc <span className="text-mono">{d.voc_no}</span>{d.app_plan_id ? <> · <span className="text-mono" title={`App dispatch plan: ${d.app_plan_id}`}>DP-{d.app_plan_id.slice(-6).toUpperCase()}</span></> : null}</> : "billed · no voucher"}
                                </div></>
                            : d.legacy
                            ? <><span className="text-mono" style={{ fontWeight: 700 }} title={`ERP Voc #${d.voc_no}`}>Voc# {d.voc_no}</span>
                                <div className="muted" style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 4, marginTop: 1 }}>
                                  <span className="jde-badge" style={{ fontSize: 8.5, background: "#FEF3C7", color: "#92400E", fontWeight: 700 }}>LEGACY</span> portal-created
                                </div></>
                            : <><span className="text-mono" style={{ fontWeight: 700 }} title={`Plan ID: ${d.plan_id}`}>DP-{d.plan_id.slice(-6).toUpperCase()}</span>
                                <div className="muted" style={{ fontSize: 10 }}>
                                  {d.voc_no ? <>ERP Voc <span className="text-mono">{d.voc_no}</span></> : "not posted"}
                                </div></>}
                        </td>
                        <td style={{ ...td, whiteSpace: "normal", maxWidth: 230 }}>
                          <span>{d.customer_name}</span>
                          {d.transfer_party && (
                            <span data-testid={`tp-pill-${d.plan_id}`} title={d.tp_name ? `Transfer-priced: billed via ${d.tp_name} (not the buyer)` : "Transfer-priced dispatch"}
                              style={{ marginLeft: 6, fontSize: 8.5, fontWeight: 800, letterSpacing: 0.3, padding: "1px 5px", borderRadius: 999, background: "#EDE9FE", color: "#6D28D9", border: "1px solid #DDD6FE", verticalAlign: "middle" }}>TP</span>
                          )}
                          {(() => {
                            const code = d.cust_code || d.pcode;
                            return code && String(code).toUpperCase() !== String(d.customer_name || "").toUpperCase() ? (
                              <span style={{ color: "#94A3B8", fontSize: 10.5, fontWeight: 600 }} title="ERP customer code"> ({code})</span>
                            ) : null;
                          })()}
                          {d.transfer_party && d.tp_name && (
                            <div style={{ fontSize: 10, color: "#7C3AED" }} title="Transfer party (not the buyer)">
                              via TP: {d.tp_name}
                            </div>
                          )}
                          {d.cust_mismatch && d.cust_mismatch.planned_name && (
                            <div style={{ fontSize: 10, color: "#B45309", display: "flex", alignItems: "center", gap: 3, marginTop: 2 }}
                              data-testid={`cust-mismatch-${d.plan_id}`}
                              title={`This dispatch was booked under "${d.cust_mismatch.planned_name}" (${d.cust_mismatch.planned_pcode}), but its invoice bills the customer shown above. Showing the invoiced (billed) customer.`}>
                              <WarningCircle size={11} weight="fill" />
                              booked under {d.cust_mismatch.planned_name}
                            </div>
                          )}
                        </td>
                        <td style={td} className="text-mono">
                          {d.cust_po2
                            ? <span data-testid={`endcustpo-${d.plan_id}`}>
                                {d.cust_po2}
                                {d.cust_po2_source && (
                                  <span title={d.cust_po2_source === "xwalk"
                                      ? "Auto-mapped from the Legrand End-Customer PO crosswalk"
                                      : "Derived from the seeded PO master"}
                                    style={{ marginLeft: 4, fontSize: 9, color: "#2563EB",
                                             border: "1px solid #BFDBFE", borderRadius: 4,
                                             padding: "0 3px", verticalAlign: "middle" }}>
                                    auto
                                  </span>
                                )}
                              </span>
                            : <span className="muted">—</span>}
                        </td>
                        <td style={td} className="text-mono">
                          {fmtDate(d.dispatch_date)}
                          {d.dispatch_date_adjusted && (
                            <span title={`Invoice date shown (voucher was re-invoiced). Portal voucher date: ${fmtDate(d._vdate_original)}`}
                              style={{ marginLeft: 4, fontSize: 9, color: "#2563EB",
                                       border: "1px solid #BFDBFE", borderRadius: 4,
                                       padding: "0 3px", verticalAlign: "middle" }}
                              data-testid={`disp-reg-invdate-${d.plan_id}`}>
                              inv
                            </span>
                          )}
                        </td>
                        <td style={td}>
                          {d.inv_no
                            ? <><span className="text-mono" style={{ fontWeight: 700 }}>{d.inv_no}</span>
                                {d.inv_note && <span className="muted" style={{ fontSize: 9.5, marginLeft: 6 }} title={`Source: ${d.inv_source}`}>{d.inv_note}</span>}</>
                            : <span className="muted" style={{ fontSize: 11 }}>— pending</span>}
                        </td>
                        <td style={{ ...td, textAlign: "right" }} className="text-mono">{num(d.tot_qty)}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 700 }} className="text-mono">{money(d.tot_amount, d.currency)}</td>
                        <td style={{ ...td, textAlign: "center" }} className="text-mono">
                          {d.must_ship_by
                            ? <span style={{ fontSize: 11, fontWeight: 700, color: "#B91C1C", whiteSpace: "nowrap" }} title="Must ship by = CFS cut-off − 4 calendar days (for CY/container-yard shipments it is the sailing date itself)" data-testid={`disp-reg-msb-${d.plan_id}`}>{fmtDate(d.must_ship_by)}</span>
                            : <span className="muted">—</span>}
                        </td>
                        <td style={{ ...td, textAlign: "center" }} className="text-mono">
                          {d.otd_target
                            ? <span style={{ fontSize: 11, fontWeight: 600, color: "#334155", whiteSpace: "nowrap" }} title="OTD target date the on-time grade is measured against" data-testid={`disp-reg-otdtarget-${d.plan_id}`}>{fmtDate(d.otd_target)}</span>
                            : <span className="muted">—</span>}
                        </td>
                        <td style={{ ...td, textAlign: "center" }}>
                          <OtdChip status={d.otd_status} days={d.otd_days} testid={`disp-reg-otd-${d.plan_id}`} />
                        </td>
                        <td style={{ ...td, textAlign: "center" }}>
                          <span className="jde-badge" style={{ background: st.bg, color: st.fg, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`disp-reg-status-${d.plan_id}`}>
                            <st.icon size={11} weight="bold" />{st.label}
                          </span>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr key={`d-${d.plan_id}`}>
                          <td colSpan={13} style={{ padding: 0, background: "#FAFCFF", borderBottom: "2px solid var(--jde-border)" }}>
                            <LineDrilldown d={d} lines={lines[d.plan_id]} busy={lineBusy[d.plan_id]} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {(loading || warming) && rows.length === 0 && (
                  <tr><td colSpan={13} className="muted" style={{ padding: 20, textAlign: "center" }} data-testid="dr-loading">
                    {warming
                      ? "Building the dispatch register from the live ERP… this first load can take up to a minute. It'll appear automatically."
                      : "Loading dispatch register… (live ERP + legacy vouchers)"}
                  </td></tr>
                )}
                {!loading && !warming && rows.length === 0 && (
                  <tr><td colSpan={13} className="muted" style={{ padding: 20, textAlign: "center" }}>No dispatch plans match.</td></tr>
                )}
              </tbody>
            </table>
            )}
          </div>
        </div>
      </div>
      {sheetOpen && (
        <SpreadsheetModal
          title="Dispatch Register"
          columns={exportColumns}
          rows={sorted}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </Layout>
  );
}

function LineDrilldown({ d, lines, busy }) {
  const th = { textAlign: "left", padding: "6px 10px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, color: "#64748B", borderBottom: "1px solid var(--jde-border)", whiteSpace: "nowrap" };
  const td = { padding: "7px 10px", fontSize: 12, borderBottom: "1px solid #EEF1F4", whiteSpace: "nowrap" };
  const cur = d.currency;
  if (busy) return <div className="muted" style={{ padding: 16, fontSize: 12 }}>Loading line items…</div>;
  const ls = lines || [];
  return (
    <div style={{ padding: "12px 18px 16px" }} data-testid={`disp-reg-drill-${d.plan_id}`}>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 10, fontSize: 11.5, color: "#334155" }}>
        <Meta icon={Package} label={d.invoice_only ? "Source" : d.legacy ? "Source" : "Dispatch Plan"} value={d.invoice_only ? (d.app_plan_id ? `Invoice · DP-${d.app_plan_id.slice(-6).toUpperCase()}` : "Invoice-only") : d.legacy ? "Legacy · portal" : `DP-${d.plan_id.slice(-6).toUpperCase()}`} />
        <Meta icon={Truck} label="ERP Voc #" value={d.voc_no || (d.invoice_only ? "n/a (invoice-only)" : "not posted")} />
        <Meta icon={FileText} label={`Invoice #${d.inv_note ? ` (${d.inv_note})` : ""}`} value={d.inv_no || "not invoiced"} />
        <Meta icon={ClockClockwise} label="OTD offset" value={`${d.offset_days || 0}d`} />
        <Meta label="Lines" value={ls.length} />
        <Meta label="Total Qty" value={num(d.tot_qty)} />
        <Meta label="Amount" value={money(d.tot_amount, cur)} />
      </div>
      <div style={{ overflowX: "auto", border: "1px solid var(--jde-border)", borderRadius: 6, background: "white" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1040 }}>
          <thead>
            <tr>
              <th style={th}>#</th>
              <th style={th}>Item</th>
              <th style={th}>Description</th>
              <th style={th}>PO #</th>
              <th style={th}>PO Date</th>
              <th style={{ ...th, textAlign: "center" }}>Release</th>
              <th style={{ ...th, textAlign: "right" }}>Ordered</th>
              <th style={{ ...th, textAlign: "right" }}>Dispatched</th>
              <th style={th}>Unit</th>
              <th style={{ ...th, textAlign: "right" }}>Rate</th>
              <th style={th}>Due Date</th>
              <th style={th}>Ship Date</th>
              <th style={{ ...th, textAlign: "center" }}>OTD</th>
              <th style={{ ...th, textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {ls.map((l, idx) => {
              const o = lineOtd(d.dispatch_date, l.deliv_date, d.offset_days);
              return (
                <tr key={idx}>
                  <td style={td} className="text-mono">{idx + 1}</td>
                  <td style={{ ...td, fontWeight: 700 }} className="text-mono">{l.icode || "—"}<WmPart icode={l.icode} /></td>
                  <td style={{ ...td, whiteSpace: "normal" }}>{l.iname || "—"}</td>
                  <td style={td}>
                    <span className="text-mono">{l.custpo || l.po || "—"}</span>
                    {l.custpo && l.po && <div className="muted" style={{ fontSize: 9.5 }} title="PO number the dispatch was booked against in the ERP">ERP PO {l.po}{l.rev ? ` · rev ${l.rev}` : ""}</div>}
                  </td>
                  <td style={td} className="text-mono">{fmtDate(l.po_date)}</td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {l.release ? <span className="jde-badge" style={{ fontSize: 10, fontWeight: 700, background: "#E0F2FE", color: "#075985" }} title="Delivery release within this PO line">{l.release}</span> : <span className="muted">—</span>}
                  </td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{l.po_qty == null ? <span className="muted" title="Ordered qty is not stored on legacy portal dispatches">—</span> : num(l.po_qty)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, color: Number(l.disp_qty) > 0 ? "#166534" : "#94A3B8" }} className="text-mono">{num(l.disp_qty)}</td>
                  <td style={td}>{l.unit || "—"}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{l.rate}</td>
                  <td style={td} className="text-mono">{fmtDate(l.deliv_date)}</td>
                  <td style={td} className="text-mono">{fmtDate(l.disp_date || l.dispatch_date || d.dispatch_date)}</td>
                  <td style={{ ...td, textAlign: "center" }}><OtdChip status={o?.status} days={o?.days} /></td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }} className="text-mono">{money(l.amt, cur)}</td>
                </tr>
              );
            })}
            {ls.length === 0 && <tr><td colSpan={14} className="muted" style={{ padding: 14, textAlign: "center" }}>No line items on this dispatch.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {Icon && <Icon size={13} weight="bold" style={{ color: "#0EA5E9" }} />}
      <span className="muted" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}:</span>
      <strong style={{ fontSize: 12 }}>{value}</strong>
    </span>
  );
}
